/**
 * The adaptive layer. Driven by `setTension(0..1)` from the director, plus the
 * player's own state.
 *
 * At zero there is nothing here but silence — the game is 90% exploration and
 * the ambience has to be allowed to carry it alone. As dread rises a sub drone
 * comes up first, then a pair of held tones a semitone apart, then a slow throb,
 * and only at the very top the detuned high strings. Every stage eases over
 * seconds and dread leaves more slowly than it arrives, so the player is never
 * told "the monster is gone" by a music cue snapping off.
 *
 * The heartbeat is separate: it follows health and sanity rather than threat,
 * so being nearly dead in an empty room is its own kind of loud.
 */

import { TUNING } from '../core/config';
import { clamp01, damp, lerp, smoothstep } from '../core/math';
import type { SurvivalStats } from '../core/types';
import type { AudioGraph } from './graph';
import { makeHarmonicWave } from './noise';
import type { Sfx } from './sfx';

interface Layers {
  out: GainNode;
  sub: GainNode;
  drone: GainNode;
  pulse: GainNode;
  strings: GainNode;
  pulseRate: AudioParam;
}

export class Tension {
  private target = 0;
  private eased = 0;
  private beat = 0;
  private beatIn = 0;
  private applyIn = 0;
  private layers: Layers | null = null;

  constructor(
    private readonly g: AudioGraph,
    private readonly sfx: Sfx,
  ) {}

  setTarget(v: number): void {
    this.target = clamp01(v);
  }

  get value(): number {
    return this.eased;
  }

  update(dt: number, stats: SurvivalStats, alive: boolean): void {
    // Dread arrives faster than it leaves.
    const rate = this.target > this.eased ? 0.5 : 0.2;
    this.eased = damp(this.eased, this.target, rate, dt);

    if (this.eased > 0.015 && !this.layers) this.layers = this.build();
    if (this.layers) {
      this.applyIn -= dt;
      if (this.applyIn <= 0) {
        this.applyIn = 0.08;
        this.applyLevels(this.layers, this.eased);
      }
    }

    this.updateHeartbeat(dt, stats, alive);
  }

  private updateHeartbeat(dt: number, stats: SurvivalStats, alive: boolean): void {
    const crit = TUNING.survival.criticalThreshold / 100;
    const health = clamp01(stats.health / 100);
    const sanity = clamp01(stats.sanity / 100);
    const fromHealth = 1 - smoothstep(crit * 0.4, crit * 1.7, health);
    const fromSanity = (1 - smoothstep(0.1, 0.42, sanity)) * 0.8;
    const drive = alive ? clamp01(Math.max(fromHealth, fromSanity, this.eased * 0.5)) : 0;
    this.beat = damp(this.beat, drive, 0.9, dt);

    if (this.beat <= 0.12) {
      this.beatIn = 0;
      return;
    }
    this.beatIn -= dt;
    if (this.beatIn <= 0) {
      this.beatIn = 60 / lerp(52, 108, this.beat);
      this.sfx.play('heartbeat', { gain: 0.2 + 0.6 * this.beat });
    }
  }

  private applyLevels(l: Layers, v: number): void {
    const t = this.g.now;
    l.sub.gain.setTargetAtTime(smoothstep(0.02, 0.4, v) * 0.09, t, 0.3);
    l.drone.gain.setTargetAtTime(smoothstep(0.22, 0.72, v) * 0.045, t, 0.35);
    l.pulse.gain.setTargetAtTime(smoothstep(0.45, 0.92, v) * 0.06, t, 0.3);
    l.strings.gain.setTargetAtTime(smoothstep(0.68, 1, v) * 0.03, t, 0.5);
    l.pulseRate.setTargetAtTime(lerp(1.05, 2.7, v), t, 0.8);
  }

  /**
   * Built on the first hint of tension rather than at boot, so a calm run never
   * pays for oscillators it will not use.
   */
  private build(): Layers {
    const ctx = this.g.ctx;
    const out = ctx.createGain();
    out.gain.value = 1;
    out.connect(this.g.bed);
    const send = ctx.createGain();
    send.gain.value = 0.32;
    out.connect(send);
    send.connect(this.g.reverbIn);

    const sub = gain(ctx, 0, out);
    for (const f of [38, 45.5]) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(sub);
      o.start();
    }

    // A semitone and a tritone: intervals the ear refuses to resolve.
    const drone = gain(ctx, 0, out);
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 700;
    droneFilter.Q.value = 3;
    droneFilter.connect(drone);
    for (const [f, d] of [
      [116, 0],
      [122.9, 6],
      [164.2, -8],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = d;
      o.connect(droneFilter);
      o.start();
      lfo(ctx, 0.07 + f * 1e-4, 4, o.detune);
    }

    // Slow throb. Two harmonics of the gate shape it into something with a
    // leading edge instead of a plain sine swell.
    const pulse = gain(ctx, 0, out);
    const gate = ctx.createGain();
    gate.gain.value = 0.4;
    gate.connect(pulse);
    const gateOsc = ctx.createOscillator();
    gateOsc.setPeriodicWave(makeHarmonicWave(ctx, [1, 0.45, 0.2]));
    gateOsc.frequency.value = 1.4;
    const gateAmp = ctx.createGain();
    gateAmp.gain.value = 0.42;
    gateOsc.connect(gateAmp);
    gateAmp.connect(gate.gain);
    gateOsc.start();

    const thumpOsc = ctx.createOscillator();
    thumpOsc.type = 'sine';
    thumpOsc.frequency.value = 41;
    thumpOsc.connect(gate);
    thumpOsc.start();
    const body = ctx.createBufferSource();
    body.buffer = this.g.brown;
    body.loop = true;
    const bodyFilter = ctx.createBiquadFilter();
    bodyFilter.type = 'lowpass';
    bodyFilter.frequency.value = 170;
    const bodyGain = ctx.createGain();
    bodyGain.gain.value = 0.5;
    body.connect(bodyFilter);
    bodyFilter.connect(bodyGain);
    bodyGain.connect(gate);
    body.start();

    // Detuned high strings, only at the very top.
    const strings = gain(ctx, 0, out);
    const stringFilter = ctx.createBiquadFilter();
    stringFilter.type = 'bandpass';
    stringFilter.frequency.value = 1400;
    stringFilter.Q.value = 1.1;
    stringFilter.connect(strings);
    for (const [f, d] of [
      [880, -18],
      [887, 11],
      [1319, 22],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = d;
      o.connect(stringFilter);
      o.start();
      lfo(ctx, 0.13 + f * 1e-5, 9, o.detune);
    }

    return { out, sub, drone, pulse, strings, pulseRate: gateOsc.frequency };
  }
}

function gain(ctx: AudioContext, value: number, dest: AudioNode): GainNode {
  const g = ctx.createGain();
  g.gain.value = value;
  g.connect(dest);
  return g;
}

function lfo(ctx: AudioContext, rate: number, depth: number, target: AudioParam): void {
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.value = rate;
  const amp = ctx.createGain();
  amp.gain.value = depth;
  o.connect(amp);
  amp.connect(target);
  o.start();
}
